import { supabase } from './supabase';

/**
 * Founding-member welcome video.
 *
 * The video is a global admin setting (a cf_stream media-row id stored in
 * app_settings, key 'welcome_video'); the per-user "seen" flag lives on
 * profiles.seen_welcome and is flipped by the mark_welcome_seen() RPC.
 *
 * Gate logic lives in App.tsx: a founding member with seen_welcome=false is
 * routed to /welcome once, right after onboarding.
 */

export interface WelcomeVideo {
  media_id: string | null;
}

/** Read the configured welcome video (or { media_id: null } if none/anything fails). */
export async function fetchWelcomeVideo(signal?: AbortSignal): Promise<WelcomeVideo> {
  try {
    const res = await fetch('/api/settings/welcome-video', { signal });
    if (!res.ok) return { media_id: null };
    const v = (await res.json()) as Partial<WelcomeVideo>;
    return { media_id: typeof v.media_id === 'string' && v.media_id ? v.media_id : null };
  } catch {
    return { media_id: null };
  }
}

/** Admin: set (or clear, with null) the welcome video media id. */
export async function saveWelcomeVideo(
  mediaId: string | null,
  accessToken: string,
): Promise<WelcomeVideo> {
  const res = await fetch('/api/settings/welcome-video', {
    method: 'PUT',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ media_id: mediaId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error ?? `Save failed (${res.status}).`);
  return data as WelcomeVideo;
}

/** Mark the current member as having seen the welcome screen (one-time). */
export async function markWelcomeSeen(): Promise<void> {
  const { error } = await supabase.rpc('mark_welcome_seen');
  if (error) throw error;
}
